import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Star, Filter, Calendar, MessageSquare, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import DoctorHeader from '@/components/doctor/DoctorHeader';
import DoctorFooterNav from '@/components/doctor/DoctorFooterNav';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useEnhancedReviews } from '@/hooks/useEnhancedReviews';
import ReviewWithReply from '@/components/doctor/reviews/ReviewWithReply';
import ReviewEvaluationBreakdown from '@/components/doctor/reviews/ReviewEvaluationBreakdown';
import ReviewTrendsChart from '@/components/doctor/reviews/ReviewTrendsChart';
import RequestReviewModal from '@/components/doctor/reviews/RequestReviewModal';

const DoctorReviewsPage = () => {
  const { 
    reviews, 
    pinnedReviewId, 
    reviewTrends,
    evaluations,
    isLoading,
    replyToReview,
    pinReview,
    translateReview,
    requestReview
  } = useEnhancedReviews();
  
  const [filterRating, setFilterRating] = useState<string>("all");
  const [showRequestReviewModal, setShowRequestReviewModal] = useState(false);
  
  const filteredReviews = reviews.filter(review => {
    if (filterRating === "all") return true;
    return review.rating === parseInt(filterRating);
  });
  
  const pinnedReview = filteredReviews.find(r => r.id === pinnedReviewId);
  const unpinnedReviews = filteredReviews.filter(r => r.id !== pinnedReviewId);
  
  const StarRating = ({ rating }: { rating: number }) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star}>
            {star <= rating ? (
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            ) : star - 0.5 === rating ? (
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 opacity-50" />
            ) : (
              <Star className="h-4 w-4 text-muted-foreground" />
            )}
          </span>
        ))}
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-background pb-20">
      <DoctorHeader />
      
      <div className="container mx-auto p-4 pb-24 pt-20">
        <div className="flex items-center justify-between gap-2 mb-6">
          <div className="flex items-center gap-2">
            <Star className="h-6 w-6 text-yellow-400 fill-yellow-400" />
            <h1 className="text-2xl font-bold">My Reviews</h1>
          </div>
          
          <Button onClick={() => setShowRequestReviewModal(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Request Review
          </Button>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <Card className="md:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle>Review Summary</CardTitle>
              <CardDescription>Your overall rating and breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center mb-6">
                <div className="text-5xl font-bold mb-2">{reviews.length > 0 ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1) : '0.0'}</div>
                <div className="flex mb-2">
                  <StarRating rating={reviews.length > 0 ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length : 0} />
                </div>
                <div className="text-sm text-muted-foreground">from {reviews.length} reviews</div>
              </div>
              
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = reviews.filter(r => r.rating === star).length;
                  const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                  
                  return (
                    <div key={star} className="flex items-center gap-2">
                      <div className="text-sm w-10 text-right">{star} stars</div>
                      <Progress value={percentage} className="h-2" />
                      <div className="text-sm w-10">
                        {Math.round(percentage)}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          
          <Card className="md:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Review Listing</CardTitle>
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <Select value={filterRating} onValueChange={setFilterRating}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter by rating" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Ratings</SelectItem>
                      <SelectItem value="5">⭐⭐⭐⭐⭐ (5 stars)</SelectItem>
                      <SelectItem value="4">⭐⭐⭐⭐ (4 stars)</SelectItem>
                      <SelectItem value="3">⭐⭐⭐ (3 stars)</SelectItem>
                      <SelectItem value="2">⭐⭐ (2 stars)</SelectItem>
                      <SelectItem value="1">⭐ (1 star)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>
        
        <div className="mb-6">
          <ReviewTrendsChart data={reviewTrends} />
        </div>
        
        <div className="mb-6">
          <ReviewEvaluationBreakdown evaluations={evaluations} />
        </div>
        
        <div className="space-y-6">
          {pinnedReview && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                Featured Review
              </h2>
              <ReviewWithReply 
                review={pinnedReview} 
                onReply={replyToReview}
                onPin={pinReview}
                onTranslate={translateReview}
                pinned={true}
              />
            </div>
          )}
          
          <div>
            <h2 className="text-lg font-semibold mb-3">All Reviews</h2>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : unpinnedReviews.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {pinnedReview ? 'No additional reviews match your filter criteria' : 'No reviews match your filter criteria'}
              </div>
            ) : (
              <div className="space-y-4">
                {unpinnedReviews.map((review) => (
                  <ReviewWithReply 
                    key={review.id}
                    review={review} 
                    onReply={replyToReview}
                    onPin={pinReview}
                    onTranslate={translateReview}
                    pinned={false}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Improve Your Ratings</CardTitle>
            <CardDescription>Tips for enhancing your patient experience and reviews</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-2">Response Time</h3>
                <p className="text-sm text-muted-foreground">
                  Quick responses to patient inquiries can significantly improve satisfaction ratings.
                </p>
              </div>
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-2">Clear Communication</h3>
                <p className="text-sm text-muted-foreground">
                  Explain procedures and options thoroughly to build trust with patients.
                </p>
              </div>
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-2">Follow-up Care</h3>
                <p className="text-sm text-muted-foreground">
                  Regular follow-ups after procedures show your commitment to patient health.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <RequestReviewModal 
        isOpen={showRequestReviewModal}
        onClose={() => setShowRequestReviewModal(false)}
        onSubmit={requestReview}
      />
      
      <DoctorFooterNav />
    </div>
  );
};

export default DoctorReviewsPage;
